<template>
  <!-- Error bar -->
  <section v-if="error">
    <br />
    <p class="error">{{ error }}</p>
  </section>

  <section v-else class="dashboard-view">
    <div class="grid-container">
      <div class="grid-x grid-margin-x">
        <!-- MAIN -->
        <div class="cell small-12 large-12 "> 
        </div>
        <div class="cell small-12 large-12 margin-top-1">
          <!-- Banner -->
          <div class="banner-carousel" style="height: 275px;">
            <div class="banner-content grid-x grid-margin-x align-middle">
              <div class="cell show-for-medium medium-4 banner__image">
                <img src="/images/support.png" alt="Person using laptop" />
              </div>
              <div class="cell small-12 medium-8 banner__copy">
                  <h1 class="white">Hi {{ identity.account.name }}!</h1>
                  <p class="white">
                  Welcome to your <strong>Onboarding Dashboard</strong> for <strong>{{ client.name }}</strong>.
                </p>
                <p class="white">Here, you can regularly check in for updates and stay on top of your onboarding tasks.</p>
                <ProjectStatusProgressBar v-if="milestoneProgress.totalMilestoneCount > 0" :currentMilestoneName="milestoneProgress.currentMilestoneName" :currentMilestone="milestoneProgress.numberOfMilestonesComplete" 
                :totalMilestones="milestoneProgress.totalMilestoneCount" />
                <div class="progress-wrap">
                  <router-link to="/milestones">
                    <span
                      class="progress-label"
                      v-if="milestoneProgress.currentMilestoneName != null"
                    >
                      {{ splitByCapitals(milestoneProgress.currentMilestoneName) }}
                      (Stage {{ Math.min(milestoneProgress.numberOfMilestonesComplete + 1, milestoneProgress.totalMilestoneCount) }}
                      of {{ milestoneProgress.totalMilestoneCount }})
                    </span>
                  </router-link>
                </div>
              </div>
            </div>
          </div>

          <!-- Scheduled Actions -->
          <main class="actions-panel">
            <!-- Header -->
            <div class="grid-x grid-margin-x align-middle actions-header margin-top-1 medium-margin-top-2">
              <div class="cell small-12 medium-auto">
                <h3>Scheduled Actions</h3>
              </div>

              <div
                class="cell small-12 medium-shrink"
                style="text-align:right"
                v-if="new Date(project.goLiveDate).getFullYear() !== 1800"
              >
                <span class="small" v-if="false">
                  Payroll due to go live:
                  <b>{{ formatDateToDDMMYYYY(project.goLiveDate) }}</b>
                </span>
              </div>
            </div>

            <!-- Cards: 3-across, with a local right-gutter reduction -->
            <div class="grid-x grid-margin-x grid-margin-y align-stretch cards-compact">
              <div v-for="card in taskCards" :key="card.id" class="cell small-12 medium-6 large-4 cards-compact__cell">
                <ActionCard :card="card" />
              </div>
            </div>
          </main>
        </div>

      </div>
    </div>
  </section>
</template>





<script setup>

import { ref, onMounted } from 'vue'

import ActionCard from '@/components/ActionCard.vue'
import DisplayChangeControl from '@/components/controls/DisplayChangeControl.vue'
import { formatDateToDDMMYYYY } from '@/helpers/dates';
import { getMilestoneProgress } from '@/helpers/milestones/milestone.js';



import { useIdentityStore } from '@/stores/identity'
import { useClientStore } from '@/stores/client'
import { useProjectStore } from '@/stores/project'
const identity = useIdentityStore()
const client = useClientStore()
const project = useProjectStore()

import { STAGE, TASK_STATUS } from '@/helpers/constants.js'

import { getProjectTasks, getDataCollectionTasks, refreshProject } from '@/services/implementationmanagement.api';

const projectError = 'Sorry, we\'ve been unable to load your project. Please contact your Qtac account manager.';

import { buildCards, buildClientDetailsCard, buildInternalCards } from '@/helpers/taskcards/cardfactory'
import ProjectStatusProgressBar from '@/components/navigation/ProjectStatusProgressBar.vue';
import { splitByCapitals } from '@/helpers/strings';

const taskCards = ref([])
const milestoneProgress = ref({
  totalMilestoneCount: 0,
  numberOfMilestonesComplete: 0,
  currentMilestoneName: null
})
const error = ref(null)

onMounted(async () => {


  try {
    await refreshProject(project.projectId);

    await displayCards()
    milestoneProgress.value = await getMilestoneProgress(project);

  } catch (err) {
    error.value = projectError;
    console.error(err);
  }


});

async function displayCards(filters = null) {
  // Clear previous cards
  taskCards.value = [];

  const projectTasksResponse = await getProjectTasks(project.projectId);
  const allProjectTasks = projectTasksResponse.data;

  await Promise.all([
    processProjectTasksCards(allProjectTasks, STAGE.STAGE_ONE.apiStageName, filters),
    processProjectTasksCards(allProjectTasks, STAGE.STAGE_TWO.apiStageName, filters),
    processProjectTasksCards(allProjectTasks, STAGE.STAGE_THREE.apiStageName, filters),
    processProjectTasksCards(allProjectTasks, STAGE.STAGE_FOUR.apiStageName, filters),
    processProjectTasksCards(allProjectTasks, STAGE.STAGE_FIVE.apiStageName, filters),
    processMilestoneCards()
  ]);

  // Sort projectCards by createdDate ascending
  taskCards.value.sort((a, b) => new Date(a.createdDate) - new Date(b.createdDate));


  /* This should always be the last action */
  await processStaticCards(filters);
}

async function processProjectTasksCards(tasks, stage, filters) {

  let filteredTasks = tasks.filter(task => task.projectStage === stage);

  if (filters != null) {
    if (filters.status) {
      filteredTasks = filteredTasks.filter(task => task.taskStatus === filters.status);
    }
    if (filters.stage) {
      filteredTasks = filteredTasks.filter(task => task.projectStage === filters.stage);
    }
  }

  taskCards.value.push(...await buildCards(filteredTasks));
}


async function processStaticCards(filters) {
  if (
    filters != null &&
    ((filters.status && filters.status !== 'SuccessfullyCompleted') ||
      (filters.stage && filters.stage !== 'StageOne'))
  )
    return;

  taskCards.value.unshift(await buildClientDetailsCard());

}

async function processMilestoneCards() {

  var internalCards = await buildInternalCards();

  if(internalCards) {
      taskCards.value.unshift(...internalCards);
  }

}



function handleFilters(filters) {
  displayCards(filters)
}



</script>
